import React, { useContext, useEffect, useState, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Content } from './style'
import { ButtonCreate, LeftSide, OptionsButton, CompanyTypes, RightSide, SectionName, SubHeaderContainer } from '../../../components/styles/subheader-style'
import TableCompanies from './CompaniesTable'
import ModalContext from '../../../contexts/ModalContext'
import FieldsManager, { FieldsManagerType } from '../../../components/FieldManager'
import { Settings2 } from 'lucide-react';
import { tableFields } from '../../../components/FieldManager/table-fields'
import WithoutSelectedFields from '../../SystemErrors/WithoutSelectedFields'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'
import FilterMenu from '../../../components/SubHeaderFilter/FilterMenu'
import { Header } from '../../../components/SubHeaderFilter/types'
import AppliedFiltersDisplay from '../../../components/SubHeaderFilter/AppliedFiltersDisplay'
import { Filter } from '../../../components/SubHeaderFilter/types'
import { RefreshIconContainer, FilterWrapper } from '../../../components/styles/subheader-style'
import { RefreshCw, Filter as FilterIcon } from 'lucide-react'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import { TypeMainOutlet } from '..'
import { useUserPreferences } from '../../../contexts/UserPreferencesContext'

const CompaniesSubHeader: React.FC<{
  appliedFilters: Filter[];
  onApplyFilter: (f: Filter) => void;
  fields: FieldsManagerType;
  personType: 'Jurídica' | 'Física' | 'Própria' | 'Transportadora' | null;
  setPersonType: React.Dispatch<React.SetStateAction<'Jurídica' | 'Física' | 'Própria' | 'Transportadora' | null>>;
  useMyPrefs?: boolean;
  useMyPrefsKey?: string;
}> = ({ appliedFilters, onApplyFilter, fields, personType, setPersonType, useMyPrefs, useMyPrefsKey }) => {
  const { setModalActive, setModalSection } = useContext(ModalContext)
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const filterButtonRef = useRef<HTMLDivElement | null>(null)
  const queryClient = useQueryClient()

  const navigate = useNavigate()
  const createCompany = () => {
    navigate('create')
  }

  const handleSetPersonType = (type: 'Jurídica' | 'Física' | 'Própria' | 'Transportadora') => {
    if (type === personType) {
      setPersonType(null)
      return
    }
    setPersonType(type)
  }

  const visibleFieldsForFilter = fields.whichFieldsToShow.filter((f: any) => f.checked)

  const baseHeaders: Header[] = visibleFieldsForFilter.map((f: any) => {
    if (f.field === 'companyTypes') {
      return { key: f.field, label: f.fieldName, type: 'string', selectOptions: [
        { value: 'Cliente', label: 'Cliente' },
        { value: 'Fornecedor', label: 'Fornecedor' },
        { value: 'Transportadora', label: 'Transportadora' },
        { value: 'Própria', label: 'Própria' }
      ] } as Header
    }
    return { key: f.field, label: f.fieldName, type: 'string' } as Header
  })

  const headerOptions = baseHeaders.filter(h => !['cnpj', 'companyName', 'cpf', 'contactName'].includes(h.key))

  if (personType === 'Jurídica') {
    headerOptions.unshift(
      { key: 'cnpj', label: 'CNPJ', type: 'string' },
      { key: 'companyName', label: 'Razão Social', type: 'string' }
    )
  } else if (personType === 'Física') {
    headerOptions.unshift(
      { key: 'cpf', label: 'CPF', type: 'string' },
      { key: 'contactName', label: 'Nome', type: 'string' }
    )
  } else {
    headerOptions.unshift(
      { key: 'cnpj', label: 'CNPJ', type: 'string' },
      { key: 'companyName', label: 'Razão Social', type: 'string' },
      { key: 'cpf', label: 'CPF', type: 'string' },
      { key: 'contactName', label: 'Nome', type: 'string' }
    )
  }


  const { setPreference, isTenantAdmin } = useUserPreferences()

  return (
    <SubHeaderContainer>
      <LeftSide>
        <SectionName>Empresas</SectionName>
        {!isTenantAdmin && useMyPrefsKey && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 600, color: '#696969', marginLeft: '16px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={useMyPrefs}
              onChange={(e) => setPreference(useMyPrefsKey, e.target.checked)}
              style={{ width: '16px', height: '16px', accentColor: '#0090D3', cursor: 'pointer' }}
            />
            Utilizar minhas preferências
          </label>
        )}
      </LeftSide>
      <RightSide>
        <FilterWrapper style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>
          <CompanyTypes onClick={() => handleSetPersonType('Jurídica')} className={personType === 'Jurídica' ? 'actived' : ''} title="Juridica">JURÍDICA</CompanyTypes>
          <CompanyTypes onClick={() => handleSetPersonType('Física')} className={personType === 'Física' ? 'actived' : ''} title="Fisica" style={{ marginLeft: 8 }}>FÍSICA</CompanyTypes>
          <CompanyTypes onClick={() => handleSetPersonType('Própria')} className={personType === 'Própria' ? 'actived' : ''} title="Propria" style={{ marginLeft: 8 }}>PRÓPRIA</CompanyTypes>
          <CompanyTypes onClick={() => handleSetPersonType('Transportadora')} className={personType === 'Transportadora' ? 'actived' : ''} title="Transportadora" style={{ marginLeft: 8 }}>TRANSPORTADORA</CompanyTypes>
        </FilterWrapper>
        <RefreshIconContainer onClick={() => { queryClient.invalidateQueries({ queryKey: ['companies'] }); }} title="Atualizar">
          <RefreshCw size={20} />
        </RefreshIconContainer>
        <FilterWrapper>
          <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
            <FilterIcon size={10} />
          </OptionsButton>
          {isFilterMenuOpen && (
            <FilterMenu
              headers={headerOptions}
              onApplyFilter={(f) => onApplyFilter(f)}
              onClose={() => setIsFilterMenuOpen(false)}
              anchorRef={filterButtonRef}
              filters={appliedFilters}
            />
          )}
        </FilterWrapper>
        <OptionsButton style={{ visibility: 'hidden' }}><Settings2 /></OptionsButton>
        <ButtonCreate onClick={() => createCompany()}>+ Cadastrar empresa</ButtonCreate>
      </RightSide>
    </SubHeaderContainer>
  )
}

const CompaniesContent = () => {
  const { getPreference, getEffectivePreference, setPreference, isTenantAdmin, isLoading: isPrefsLoading } = useUserPreferences()
  const useMyPrefsKey = 'useMyPreferences_tableFields'
  const useMyPrefs = getPreference<boolean>(useMyPrefsKey, false)
  const [isdropdownOpen, setIsDropdownOpen] = useState(false)
  const [fields, setFields] = useState<FieldsManagerType>(
    tableFields.find(field => field.tableSection === 'companies') || tableFields[0]
  )

  useEffect(() => {
    if (isPrefsLoading) return
    const fromLocalStorage = getEffectivePreference<FieldsManagerType[] | undefined>('tableFields')
    const defaultCompanies = tableFields.filter(field => field.tableSection === 'companies')[0]
    if (fromLocalStorage) {
      try {
        const storedCompanies = fromLocalStorage.find((t: any) => t.tableSection === 'companies')
        if (storedCompanies) {
          const merged = {
            ...defaultCompanies,
            whichFieldsToShow: defaultCompanies.whichFieldsToShow.map(df => {
              const found = storedCompanies.whichFieldsToShow.find((sf: any) => sf.field === df.field)
              return found ? { ...df, checked: found.checked } : df
            })
          }
          setFields(merged)
        }
      } catch (e) {
        console.error('Failed parsing tableFields', e)
      }
    }
  }, [isPrefsLoading, getEffectivePreference])

  const { search } = useOutletContext<TypeMainOutlet>()
  const [refresh, setRefresh] = useState(false);
  const {
    appliedFilters,
    handleApplyFilter,
    handleRemoveFilter,
    handleClearAll
  } = usePersistedFilters('companies')
  const [personType, setPersonType] = useState<'Jurídica' | 'Física' | 'Própria' | 'Transportadora' | null>(null)

  const handleSaveFields = useCallback((value: React.SetStateAction<FieldsManagerType>) => {
    setFields((prev) => {
      const newFields = typeof value === 'function' ? (value as any)(prev) : value

      const fromLocalStorage = getEffectivePreference<FieldsManagerType[] | undefined>('tableFields')
      const tableFieldsJson = fromLocalStorage || tableFields
      const toLocalStorage = tableFieldsJson.map((table: FieldsManagerType) => {
        if (table.tableSection === 'companies') {
          return { ...table, whichFieldsToShow: newFields.whichFieldsToShow }
        }
        return table
      })

      setTimeout(() => {
        setPreference('tableFields', toLocalStorage)
        if (!isTenantAdmin) {
          setPreference(useMyPrefsKey, true)
        }
      }, 0)
      return newFields
    })
  }, [getEffectivePreference, isTenantAdmin, useMyPrefsKey, setPreference])

  const checkedFieldsCount = fields.whichFieldsToShow.filter((field: any) => field.checked).length

  const handleAddFilter = (f: Filter) => {
    let cleanedValue = typeof f.value === 'string' ? f.value.replace(/\s+/g, ' ').trim() : f.value;
    let cleanedDisplay = typeof f.displayValue === 'string' ? String(f.displayValue).replace(/\s+/g, ' ').trim() : f.displayValue;

    if (f.key === 'address' && typeof cleanedValue === 'string') {
      const digits = cleanedValue.replace(/\D/g, '');
      if (digits.length > 0) {
        const padded = digits.padStart(8, '0');
        cleanedValue = digits;
        cleanedDisplay = `${padded.slice(0, 5)}-${padded.slice(5)}`;
      }
    }

    const cleanedFilter = { ...f, value: cleanedValue, displayValue: cleanedDisplay };
    handleApplyFilter(cleanedFilter);
  }

  useEffect(() => {
    // Ao mudar o filtro rápido (Jurídica/Física) ajusta automaticamente as colunas relevantes
    const juridicaFields = ['cnpj', 'companyName', 'stateRegistration'];
    const fisicaFields = ['cpf', 'contactName'];

    if (!personType || personType === 'Própria' || personType === 'Transportadora') return

    setFields(prev => ({
      ...prev,
      whichFieldsToShow: prev.whichFieldsToShow.map(f => {
        if (personType === 'Jurídica' && juridicaFields.includes(f.field)) {
          return { ...f, checked: true }
        }
        if (personType === 'Física' && fisicaFields.includes(f.field)) {
          return { ...f, checked: true }
        }
        if (personType === 'Jurídica' && fisicaFields.includes(f.field)) {
          return { ...f, checked: false }
        }
        if (personType === 'Física' && juridicaFields.includes(f.field)) {
          return { ...f, checked: false }
        }
        return f
      })
    }))
  }, [personType])

  return (
    <>
      <CompaniesSubHeader appliedFilters={appliedFilters} onApplyFilter={handleAddFilter} fields={fields} personType={personType} setPersonType={setPersonType} useMyPrefs={useMyPrefs} useMyPrefsKey={useMyPrefsKey} />
      <AppliedFiltersDisplay
        filters={appliedFilters}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={() => {
          handleClearAll()
          setPersonType(null)
        }}
      />
      <Content>
        {checkedFieldsCount === 0 ?
          <WithoutSelectedFields dropdown={setIsDropdownOpen} />
          :
          <>
            <TableCompanies
              dropdownOpen={setIsDropdownOpen}
              isDropdownOpen={isdropdownOpen}
              whichFieldsToShow={fields}
              setFields={handleSaveFields}
              appliedFilters={appliedFilters}
              refresh={refresh}
              personType={personType}
              search={search}
            />
          </>
        }
        <Outlet
          context={search}
        />
      </Content>
    </>
  )
}
export default CompaniesContent
